<template>
  <tr class="tw:align-middle">
    <td>
      <div class="tw:flex tw:items-start tw:gap-2.5">
        <span class="tw:mt-0.5 tw:flex tw:size-5.5 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:bg-teal-600 tw:text-white"
              :title="message.channel === 'sms' ? $t('SMS') : $t('Email')">
          <component :is="message.channel === 'sms' ? Smartphone : Mail" class="tw:size-3.5" aria-hidden="true" />
        </span>
        <div class="tw:min-w-0">
          <a v-if="message.links.self" :href="message.links.self"
             class="tw:font-medium tw:text-zinc-900 tw:no-underline tw:hover:underline">{{ message.name }}</a>
          <span v-else class="tw:font-medium tw:text-zinc-900">{{ message.name }}</span>

          <div v-if="message.trigger || message.workflow" class="tw:mt-1 tw:flex tw:flex-wrap tw:items-center tw:gap-1.5">
            <span v-if="message.trigger" data-role="trigger"
                  class="tw:inline-flex tw:max-w-full tw:items-center tw:gap-1.5 tw:rounded-md tw:border tw:border-violet-300 tw:bg-white tw:py-0.5 tw:pr-2 tw:pl-1 tw:text-xs tw:text-zinc-700">
              <span class="tw:flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-violet-500 tw:text-white" aria-hidden="true">
                <Zap class="tw:size-2.5" />
              </span>
              <span class="tw:truncate">{{ message.trigger }}</span>
            </span>
            <span v-if="message.workflow" data-role="workflow"
                  class="tw:inline-flex tw:items-center tw:gap-1 tw:text-xs tw:text-zinc-500">
              <WorkflowIcon class="tw:size-3.5" aria-hidden="true" /> {{ message.workflow.name }}
            </span>
          </div>
        </div>
      </div>
    </td>

    <td data-column="sending" class="tw:hidden tw:text-zinc-900 tw:md:table-cell">
      <span class="tw:inline-flex tw:items-center tw:gap-1.5 tw:whitespace-nowrap">
        <component :is="typeIcon" data-role="sending-icon" class="tw:size-3.5 tw:text-zinc-400" aria-hidden="true" />
        {{ sending }}
      </span>
      <div v-if="message.sending_until" class="tw:text-xs tw:text-zinc-500">
        {{ $t('Until') }} {{ message.sending_until }}
      </div>
    </td>

    <td data-column="sent" class="tw:hidden tw:md:table-cell">
      <div class="tw:flex tw:items-center tw:gap-2 tw:whitespace-nowrap tw:text-zinc-900">
        <span class="tw:h-1 tw:w-12 tw:overflow-hidden tw:rounded-full tw:bg-zinc-200" aria-hidden="true">
          <span class="tw:block tw:h-full tw:bg-teal-600" :style="{ width: `${share(message.sent_count, message.recipients_count)}%` }"></span>
        </span>
        {{ message.sent_count }} / {{ message.recipients_count }}
      </div>
      <div v-if="message.dynamic_contents" data-role="dynamic-contents" class="tw:text-xs tw:whitespace-nowrap"
           :class="lacksDynamicContents ? 'tw:text-red-600' : 'tw:text-zinc-500'">
        {{ $t('Dynamic contents') }} {{ message.dynamic_contents.used }} / {{ message.dynamic_contents.total }}
      </div>
    </td>

    <td data-column="status" class="tw:whitespace-nowrap">
      <Badge :variant="statusVariant">
        {{ message.processing ? $t('Processing') : message.status_label }}
      </Badge>
    </td>

    <td data-column="updated" class="tw:hidden tw:align-middle tw:whitespace-nowrap tw:text-xs tw:text-zinc-500 tw:lg:table-cell">
      <span v-if="message.updated" :title="message.updated.date">{{ message.updated.ago }}</span>
    </td>

    <td>
      <div class="tw:flex tw:items-center tw:justify-end tw:gap-1">
        <Button v-if="nextStep" variant="outline" size="sm" class="tw:no-underline tw:hover:no-underline" as-child>
          <a :href="nextStep.url"><component :is="nextStep.icon" aria-hidden="true" /> {{ nextStep.label }}</a>
        </Button>

        <DropdownMenu v-if="actions.length || message.links.destroy">
          <DropdownMenuTrigger as-child>
            <Button variant="ghost" size="icon" class="tw:size-8 tw:text-zinc-500" :aria-label="$t('Actions')">
              <EllipsisVertical class="tw:size-4" aria-hidden="true" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem v-for="action in actions" :key="action.label" as-child>
              <a :href="action.url"><component :is="action.icon" aria-hidden="true" /> {{ action.label }}</a>
            </DropdownMenuItem>
            <template v-if="message.links.destroy">
              <DropdownMenuSeparator v-if="actions.length" />
              <DropdownMenuItem variant="destructive" @select="confirmingDeletion = true">
                <Trash2 aria-hidden="true" /> {{ $t('Delete') }}
              </DropdownMenuItem>
            </template>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <ConfirmDialog v-if="message.links.destroy"
                     v-model:open="confirmingDeletion"
                     :title="$t('Delete the message')"
                     :message="$t('{name} will no longer be sent.', { name: message.name })"
                     :action="message.links.destroy"
                     method="DELETE" mode="request" severity="danger"
                     :confirm-label="$t('Delete')"
                     @success="emit('deleted')" />
    </td>
  </tr>
</template>

<script setup lang="ts">
import { computed, ref, type Component } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  CalendarClock, ChartNoAxesCombined, Copy, EllipsisVertical, Hand, History, Hourglass, Mail, Pencil, Send, Smartphone, Trash2,
  Workflow as WorkflowIcon, Zap,
} from '@lucide/vue'
import { Button } from '@/shadcn/components/ui/button'
import { Badge, type BadgeVariants } from '@/components/ui/badge'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/shadcn/components/ui/dropdown-menu'
import ConfirmDialog from '@/components/ui/confirm/ConfirmDialog.vue'
import type { ListedMessage } from './types'

/**
 * A message of the list. Its first cell borrows from the workflows view: the trigger as a small
 * trigger card, and the workflow holding the message.
 */
const props = defineProps<{
  message: ListedMessage;
}>()

const emit = defineEmits<{
  deleted: [];
}>()

const { t } = useI18n()

const confirmingDeletion = ref(false)

interface Action { label: string; url: string; icon: Component }

/** An automatic message tells whether it goes with its trigger, ahead of it or after a delay. */
const typeIcon = computed(() => {
  if (props.message.type === 'automatic') {
    const offset = props.message.sending_offset ?? 0

    if (offset < 0) return History
    if (offset > 0) return Hourglass

    return Zap
  }

  return props.message.type === 'scheduled' ? CalendarClock : Hand
})

/** When it goes out; a manual message goes when the team sends it. */
const sending = computed(() => {
  if (props.message.type === 'manual') {
    return props.message.sent_at ?? t('Manual')
  }

  return props.message.sending
})

const nextStep = computed<Action | null>(() => {
  const { links } = props.message

  switch (props.message.next_step) {
    case 'report': return { label: t('View report'), url: links.report, icon: ChartNoAxesCombined }
    case 'send': return { label: t('Send'), url: links.send, icon: Send }
    case 'finalize': return links.finalize ? { label: t('Edit'), url: links.finalize, icon: Pencil } : null
    default: return null
  }
})

/** The menu holds what the button beside it does not. */
const actions = computed<Action[]>(() => [
  ...(props.message.links.edit && props.message.next_step !== 'finalize'
    ? [{ label: t('Edit'), url: props.message.links.edit, icon: Pencil }]
    : []),
  ...(props.message.links.copy ? [{ label: t('Copy'), url: props.message.links.copy, icon: Copy }] : []),
])

const statusVariant = computed<BadgeVariants['variant']>(() => {
  if (props.message.processing) return 'yellow'

  return ({
    draft: 'zinc-light',
    ready: 'blue',
    inProgress: 'blue',
    sent: 'green',
  } as const)[props.message.status]
})

const lacksDynamicContents = computed(() => {
  const contents = props.message.dynamic_contents

  return contents !== null && contents.expected > contents.total
})

const share = (part: number, whole: number): number => (whole > 0 ? Math.round((part / whole) * 100) : 0)
</script>
